import { LEGACY_API_CACHE_NAME } from './runtimeCaching';

/**
 * GH#1771, segunda mitad (2026-09-29, TINA-RDIINFRA): el SW se registra A MANO,
 * sin `virtual:pwa-register`.
 *
 * Medido en prod: con #1774 ya desplegado la página se siguió recargando sola
 * ~16 s después de abrirla, al activarse el SW de 95aacead. No era nuestro
 * código: el runtime de vite-plugin-pwa (`dist/client/build/register.js:38-42`,
 * v0.20.5) hace `window.location.reload()` en el evento `activated` cuando
 * `registerType` es 'autoUpdate' -- incondicional, ningún callback lo evita. Y en
 * modo 'prompt' tiene otro camino a `reload()` (`:52-54`) que se activa si el SW
 * pasa 200 ms o más en `installed` (workbox-window `Workbox.ts:488-507`): una
 * carrera, no una garantía.
 *
 * Registrar con la API del navegador saca ese runtime del bundle, y con él los
 * dos caminos a `reload()`. Todo lo que usábamos de `registerSW` ya era nuestro:
 * el poll de update, el aviso con banner y la recarga en `vite:preloadError`.
 * `injectRegister: null` (vite.config.ts) ya impedía que el plugin inyecte su
 * propio registro. La ruta y el scope son los que el plugin emite: `/sw.js`, `/`.
 */
export const SW_URL = '/sw.js';
export const SW_SCOPE = '/';

/**
 * GH#1763 [TENANT FIRST]: el SW ya no guarda /api/ (runtimeCaching.ts), pero
 * los navegadores que usaron un build anterior TIENEN `api-cache` en disco,
 * con datos del tenant y clave solo por URL. Sacar la regla no la borra:
 * `cleanupOutdatedCaches` solo limpia el precache y la expiración de Workbox
 * solo corre cuando la ruta se usa. La borramos en cada arranque de la app.
 * Idempotente: si no existe, `caches.delete` devuelve false y no pasa nada.
 * Corre también en development: no hay razón para conservar datos de API.
 */
export async function purgeLegacyApiCache(): Promise<boolean> {
  if (typeof caches === 'undefined') return false;
  try {
    return await caches.delete(LEGACY_API_CACHE_NAME);
  } catch {
    // Cache Storage no disponible (modo privado estricto, contexto no seguro):
    // no hay nada que borrar ni un error que mostrarle al usuario.
    return false;
  }
}

// #1416 (2026-09-03, letra TINA-RDICTO) — CAC midió: bundle JS viejo servido
// 200 desde el precache del SW mientras el servidor ya daba 404 para ese
// chunk. Mecanismo completo: la pestaña abierta corre el index viejo → pide
// chunks por hash viejo → el SW nuevo ya no los precachea → 404 o bundle
// mezclado. "Vuelve a fallar tras el fix" (visto varias veces hoy) es
// exactamente esto.
//
// Causa raíz medida, NO las que parecían obvias primero: api-cache ya era
// NetworkFirst (no CacheFirst), skipWaiting+clientsClaim ya eran true en
// prod (Fix #77) y el reload en controllerchange ya existía. Lo que
// faltaba: `injectRegister: 'auto'` (vite.config.ts) inyecta un script MUDO
// que solo hace `navigator.serviceWorker.register(...)` una vez al cargar,
// sin re-chequeo periódico. El navegador solo revisa si hay un SW nuevo en
// cada NAVEGACIÓN de documento completa, y un dashboard SPA con routing
// client-side puede quedar horas con la misma pestaña abierta sin generar
// ninguna -- un deploy nuevo quedaba invisible para esa pestaña
// indefinidamente.
//
// Letra: (a) registrar via `virtual:pwa-register` (`registerSW`) con
// handler explícito en vez de dejarlo mudo; (b) nada de
// `injectRegister:'auto'` (ver vite.config.ts). La recarga automática al
// activarse el SW nuevo que se agregó acá quedó REEMPLAZADA en GH#1771: borraba
// formularios a medio llenar. Ahora avisa con banner, y el caso de #1416 (chunk
// viejo en 404) recarga en `vite:preloadError` -- ver más abajo.
const UPDATE_CHECK_INTERVAL_MS = 60_000;

// Módulo-scope a propósito (no una closure creada dentro de
// startPeriodicUpdateCheck): así `document.addEventListener` recibe SIEMPRE
// la misma referencia de función y el navegador la de-duplica sola si algo
// llama a `setupServiceWorkerListeners` más de una vez -- no se acumulan
// listeners fantasma.
let currentRegistration: ServiceWorkerRegistration | undefined;

function checkForServiceWorkerUpdate() {
  if (document.visibilityState !== 'visible') return;
  currentRegistration?.update().catch(() => {
    // Silencioso a propósito: un chequeo de update que falla (offline, red
    // inestable) no es un error del usuario -- el próximo intervalo o el
    // próximo foco lo vuelve a intentar.
  });
}

function startPeriodicUpdateCheck(registration: ServiceWorkerRegistration) {
  currentRegistration = registration;
  checkForServiceWorkerUpdate();
  document.addEventListener('visibilitychange', checkForServiceWorkerUpdate);
  setInterval(checkForServiceWorkerUpdate, UPDATE_CHECK_INTERVAL_MS);
}

/**
 * GH#1771 (2026-09-29, letra TINA-RDICTO): el SW nuevo NO recarga la página
 * solo. Antes `controllerchange` hacía `reload()` sin preguntar y borraba
 * cualquier formulario a medio llenar cuando entraba un deploy (medido en prod:
 * el login se vació con el deploy 837c3d5d). Ahora avisa con el banner
 * «Nueva versión disponible» (`PWAUpdateBanner`) y recarga al tocar.
 */
export const UPDATE_AVAILABLE_EVENT = 'pwa:update-available';

function announceUpdateAvailable() {
  window.dispatchEvent(new CustomEvent(UPDATE_AVAILABLE_EVENT));
}

/**
 * #1416 sigue cubierto, pero en el momento en que el daño ocurre: una pestaña
 * con el index viejo pide un chunk lazy con hash viejo, el servidor da 404 y
 * Vite emite `vite:preloadError`. Ahí sí recargamos: sólo pasa al navegar a
 * una ruta lazy, o sea cuando el usuario ya estaba dejando la pantalla.
 *
 * Guarda anti-bucle: si el chunk falta de verdad (no por versión vieja), la
 * recarga no lo arregla y volvería a fallar. Una sola recarga por ventana de
 * 30 s; si falla otra vez dentro de la ventana, se deja pasar el error.
 */
const PRELOAD_RELOAD_KEY = 'pwa:preload-reload-at';
const PRELOAD_RELOAD_WINDOW_MS = 30_000;

export function handlePreloadError(event: Event): boolean {
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY) ?? 0);
  } catch {
    // sessionStorage no disponible: sin memoria de la recarga previa, igual
    // se intenta una vez (Date.now() - 0 supera la ventana).
  }
  if (Date.now() - last < PRELOAD_RELOAD_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
  } catch {
    /* idem */
  }
  event.preventDefault();
  window.location.reload();
  return true;
}

/**
 * GH#2116 (2026-09-30, letra TINA-RDICTO, opción [A]) — en las rutas del alta la versión nueva
 * se activa SOLA. Medido por MUSE en el sello de #2114: un visitante que ya tenía `/signup`
 * abierto antes del deploy seguía viendo el bundle viejo («Agencia Profesional $99 … cobro
 * 14-oct») hasta tocar «Actualizar». `skipWaiting` + `clientsClaim` ya estaban en `true`
 * (vite.config.ts): el SW nuevo SÍ toma el control, pero el JS que corre en la pestaña es el que
 * ya estaba en memoria. Lo único que faltaba era volver a cargar la página.
 *
 * Regla, para no reabrir GH#1771 (recargar vaciaba formularios a medio llenar):
 *   - sólo en `/signup` y `/onboarding` (`esRutaDelAlta`);
 *   - sólo si la persona NO ha tocado nada en esta página (`usuarioInteractuo`: un `input`,
 *     `change`, `keydown` o `paste` basta para NO recargar). Una página recién cargada o quieta
 *     no tiene nada que perder;
 *   - una sola recarga por ventana de 30 s (anti-bucle, igual que `vite:preloadError`).
 * Si ya escribió algo, o está en otra ruta, queda el banner «Nueva versión disponible» de siempre.
 */
const RUTAS_DEL_ALTA = ['/signup', '/onboarding'];

export function esRutaDelAlta(pathname: string): boolean {
  return RUTAS_DEL_ALTA.some((r) => pathname === r || pathname.startsWith(`${r}/`));
}

const EVENTOS_DE_INTERACCION = ['input', 'change', 'keydown', 'paste'] as const;
let usuarioInteractuo = false;

// Módulo-scope a propósito, igual que checkForServiceWorkerUpdate: `addEventListener` de-duplica
// la misma referencia si `setupServiceWorkerListeners` corre más de una vez.
function marcarInteraccion() {
  usuarioInteractuo = true;
}

/** Sólo para tests: el flag vive en el módulo y sobrevive entre casos del mismo archivo. */
export function resetInteraccionParaTests() {
  usuarioInteractuo = false;
}

const UPDATE_RELOAD_KEY = 'pwa:update-reload-at';
const UPDATE_RELOAD_WINDOW_MS = 30_000;

/** Recarga si estamos en el alta y la persona no ha tocado nada. Devuelve true si recargó. */
export function recargarSiEsElAltaSinTocar(): boolean {
  if (!esRutaDelAlta(window.location.pathname)) return false;
  if (usuarioInteractuo) return false;
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(UPDATE_RELOAD_KEY) ?? 0);
  } catch {
    // sessionStorage no disponible: sin memoria de la recarga previa, igual se intenta una vez.
  }
  if (Date.now() - last < UPDATE_RELOAD_WINDOW_MS) return false;
  try {
    sessionStorage.setItem(UPDATE_RELOAD_KEY, String(Date.now()));
  } catch {
    /* idem */
  }
  window.location.reload();
  return true;
}

/**
 * Registra el Service Worker con la API del navegador, arma el poll de
 * actualización, avisa (sin recargar) cuando hay un SW nuevo y recarga sólo
 * si falla la carga de un chunk (#1416).
 */
export const setupServiceWorkerListeners = () => {
  void purgeLegacyApiCache();

  if (import.meta.env.MODE === 'development') {
    return;
  }

  window.addEventListener('vite:preloadError', handlePreloadError);
  for (const tipo of EVENTOS_DE_INTERACCION) {
    document.addEventListener(tipo, marcarInteraccion, { capture: true, passive: true });
  }

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .register(SW_URL, { scope: SW_SCOPE })
      .then((registration) => startPeriodicUpdateCheck(registration))
      .catch(() => {
        // Sin SW la app funciona igual (sin offline ni aviso de versión nueva):
        // un registro fallido no es un error que mostrarle al usuario.
      });

    // Sin controller al arrancar = primera instalación (clientsClaim toma la
    // página): eso no es una versión nueva, no hay nada que avisar.
    let hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // GH#2116: en el alta sin tocar nada se recarga sola; en cualquier otro caso, el banner.
      if (hadController && !recargarSiEsElAltaSinTocar()) announceUpdateAvailable();
      hadController = true;
    });

    // Compat: mensaje custom para un banner de actualización, si algún día
    // se prefiere pedir confirmación en vez de recargar solo.
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        announceUpdateAvailable();
      }
    });
  }
};
